import {
  TooltipProps as MuiTooltipProps,
  tooltipClasses,
} from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import getReactElementRef from "@mui/utils/getReactElementRef";
import useForkRef from "@mui/utils/useForkRef";
import { Tooltip as MuiTooltip } from "components/mui-core";
import React, { ReactNode } from "react";
import { isElementClamped } from "../ClampedText/ClampedText";
import { whiteColor } from "../../vendor/material-dashboard-pro-react-v1/assets/jss/material-dashboard-pro-react";
import {
  TypographyBodySmall,
  TypographyLabelSmall,
  TypographyTitleSmall,
} from "../Typography/Typography";
import { FIGMA_COLORS } from "../styles/colors";

export interface TooltipContentProps {
  title?: ReactNode;
  content: ReactNode;
  footer?: ReactNode;
}

export interface Props extends Omit<MuiTooltipProps, "title" | "ref"> {
  tooltipContent?: TooltipContentProps;
  title?: string | React.ReactElement;
  tooltipSx?: React.CSSProperties;
  disableListeners?: boolean;
  underline?: boolean;
  onlyWhenTruncated?: boolean;
}

const TooltipContent = ({ title, content, footer }: TooltipContentProps) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
    {title && <TypographyTitleSmall>{title}</TypographyTitleSmall>}
    <TypographyBodySmall>{content}</TypographyBodySmall>
    {footer && <TypographyLabelSmall>{footer}</TypographyLabelSmall>}
  </div>
);

export const Tooltip = React.forwardRef<HTMLElement, Props>(function Tooltip(
  {
    tooltipContent,
    title,
    children,
    placement = "top",
    tooltipSx,
    disableListeners,
    underline,
    onlyWhenTruncated,
    ...rest
  },
  ref
) {
  const [isTruncated, setIsTruncated] = React.useState(false);
  const anchorRef = React.useRef<HTMLElement>(null);

  const muiTooltipTitle = title ? (
    title
  ) : tooltipContent?.content ? (
    <TooltipContent {...tooltipContent} />
  ) : (
    ""
  );

  const anchor = underline ? (
    <span style={{ textDecoration: "underline", textDecorationStyle: "dotted", cursor: "help" }}>
      {children}
    </span>
  ) : (
    children
  );

  const anchorOwnRef = React.isValidElement(anchor)
    ? getReactElementRef(anchor)
    : null;
  const forkedRef = useForkRef(anchorRef, ref, anchorOwnRef);

  const wrappedChildren =
    (onlyWhenTruncated || !!ref) && React.isValidElement(anchor)
      ? React.cloneElement(anchor as React.ReactElement<any>, {
          ref: forkedRef,
          ...(onlyWhenTruncated
            ? {
                onMouseEnter: (ev: React.MouseEvent) => {
                  setIsTruncated(isElementClamped(anchorRef.current));
                  (anchor as React.ReactElement<any>).props.onMouseEnter?.(ev);
                },
              }
            : {}),
        })
      : anchor;

  if (!muiTooltipTitle) return <>{wrappedChildren}</>;

  return (
    <MuiTooltip
      title={onlyWhenTruncated && !isTruncated ? "" : muiTooltipTitle}
      placement={placement}
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: FIGMA_COLORS.inverseSurfaceLight,
            padding: "4px 8px",
            maxWidth: "35vw",
            borderRadius: "4px",
            minHeight: "24px",
            ...(tooltipSx ?? {}),
          },
        },
      }}
      disableHoverListener={disableListeners}
      disableFocusListener={disableListeners}
      disableTouchListener={disableListeners}
      {...rest}>
      {wrappedChildren}
    </MuiTooltip>
  );
});

export const LightTooltip = styled(
  ({ className, children, ...props }: Props) => {
    return (
      <Tooltip
        {...props}
        classes={{ popper: className }}>
        {children}
      </Tooltip>
    );
  }
)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: whiteColor,
    color: `${FIGMA_COLORS.onBackgroundLight}`,
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));
